import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Calendar, Clock, TrendingUp, Bell, Download, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { useLang } from '@/contexts/LangContext';
import useSystemNotifications from '@/hooks/useSystemNotifications';
import { formatDate, formatMoney } from '@/lib/utils';
import { toast } from 'sonner';

function paymentStatusLabel(status) {
  const value = String(status || '').replace(/_/g, ' ');
  if (!value) return '-';
  return value.split(' ').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

function sessionStateLabel(state) {
  if (state === 'in_progress') return 'In Progress';
  if (state === 'postponed') return 'Postponed';
  return 'Scheduled';
}

export default function StudentDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const {
    notifications,
    unreadCount,
    permissionState,
    requestPermission,
    markDone,
  } = useSystemNotifications({ enabled: Boolean(user) });

  useEffect(() => {
    if (!user && !authLoading) { navigate('/login'); return; }
    if (!user) return;
    api.get('/v1/student/summary')
      .then(res => setSummary(res.data))
      .catch(() => {
        setSummary({
          current_level: '',
          next_class: '-',
          attendance_percentage: 0,
          total_classes: 0,
          classes_attended: 0,
          enrolled_courses: [],
          level_history: [],
          notifications: [],
          payments: [],
          attendance_history: [],
        });
      })
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate, lang]);

  const enableDesktopNotifications = async () => {
    const status = await requestPermission();
    if (status === 'denied') {
      toast.error('Browser notifications are blocked. Please allow them in browser settings.');
    } else if (status === 'granted') {
      toast.success('Desktop notifications enabled.');
    } else if (status === 'unsupported') {
      toast.error('This browser does not support desktop notifications.');
    }
  };

  const downloadInvoice = async (payment) => {
    if (!payment?.invoice_pdf_url) return;
    try {
      const response = await api.get(payment.invoice_pdf_url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${payment.description || 'invoice'}.pdf`.replace(/[\\/:*?"<>|]+/g, '_');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error(t('alerts.generic_error'));
    }
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse space-y-4 w-full max-w-4xl px-4">
        <div className="h-8 bg-muted rounded w-1/3" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-28 bg-muted rounded-2xl" />)}
        </div>
      </div>
    </div>
  );

  if (!summary) return null;
  const hasCourses = Array.isArray(summary.enrolled_courses) && summary.enrolled_courses.length > 0;
  const upcomingClasses = Array.isArray(summary.upcoming_classes) ? summary.upcoming_classes : [];
  const hasLevel = summary.current_level && !['-', 'N/A', 'Unknown'].includes(String(summary.current_level).trim());
  const showExplore = !hasCourses || !hasLevel;

  return (
    <div data-testid="student-dashboard" className="py-6 sm:py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold">{t("dashboard.welcome")} <span className="text-primary">{user?.first_name}</span></h1>
            <p className="text-sm text-muted-foreground mt-1">{t('dashboard.overview')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-lg gap-2" onClick={enableDesktopNotifications}>
              <Bell className="h-4 w-4" />
              {permissionState === 'granted' ? 'Notifications On' : 'Enable Notifications'}
              {unreadCount > 0 ? <Badge variant="secondary">{unreadCount}</Badge> : null}
            </Button>
            <Badge variant="outline" className="rounded-lg">{user?.role}</Badge>
          </div>
        </div>

        {showExplore && (
          <Card className="rounded-2xl mb-6 border-primary/20 bg-primary/5">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t('dashboard.explore_title')}</p>
                  <p className="text-xs text-muted-foreground">{t('dashboard.explore_subtitle')}</p>
                </div>
              </div>
              <Link to="/courses">
                <Button className="rounded-xl" size="sm">{t('dashboard.explore_action')}</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="rounded-2xl" data-testid="student-dashboard-level-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">{t('dashboard.current_level')}</span>
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
              <p className="text-3xl font-bold text-primary">{summary.current_level || '-'}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('dashboard.intermediate')}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">{t('dashboard.attendance')}</span>
                <BarChart3 className="h-4 w-4 text-primary" />
              </div>
              <p className="text-3xl font-bold">{summary.attendance_percentage}%</p>
              <p className="text-xs text-muted-foreground mt-1">{summary.classes_attended}/{summary.total_classes} {t('common.classes')}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Present {summary.present_count || 0} / Late {summary.late_count || 0} / Absent {summary.absent_count || 0}
              </p>
              <Progress value={summary.attendance_percentage} className="h-1.5 mt-2" data-testid="student-dashboard-attendance-chart" />
            </CardContent>
          </Card>
          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">{t('dashboard.next_class')}</span>
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <p className="text-base font-semibold">{summary.next_class}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('dashboard.dont_miss')}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-8 rounded-2xl" data-testid="student-dashboard-upcoming-classes">
          <CardContent className="p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold">{t('dashboard.upcoming_classes')}</h2>
                <p className="text-xs text-muted-foreground">{t('dashboard.upcoming_classes_subtitle')}</p>
              </div>
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            {upcomingClasses.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('dashboard.no_upcoming_classes')}</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {upcomingClasses.map((session) => (
                  <div key={session.id} className="rounded-xl border bg-muted/20 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">{session.course || '-'}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{session.group || session.teacher || '-'}</p>
                      </div>
                      <Badge variant={session.state === 'in_progress' ? 'default' : 'secondary'} className="text-xs">
                        {sessionStateLabel(session.state)}
                      </Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      <span>{formatDate(session.date)} | {session.start_time_label} - {session.end_time_label}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="courses" className="space-y-6">
          <TabsList className="rounded-xl bg-muted/50">
            <TabsTrigger value="courses" className="rounded-lg">{t('dashboard.my_courses')}</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-lg">{t('dashboard.payments')}</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-lg">
              {t('dashboard.notifications')}{unreadCount > 0 ? ` (${unreadCount})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="courses">
            <div className="grid gap-4">
              {!hasCourses && (
                <Card className="rounded-2xl border-dashed">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm font-semibold">{t('dashboard.no_courses_title')}</p>
                    <p className="text-xs text-muted-foreground mt-2">{t('dashboard.no_courses_subtitle')}</p>
                    <Link to="/courses">
                      <Button className="rounded-xl mt-4" size="sm">{t('dashboard.explore_action')}</Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
              {summary.enrolled_courses.map((course, i) => (
                <Card key={i} className="rounded-2xl">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <BookOpen className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm">{course.title}</h3>
                          <p className="text-xs text-muted-foreground">{t('dashboard.level_label')}: {course.level} | {t('dashboard.next_label')}: {course.next_session}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">{course.progress}%</Badge>
                    </div>
                    <Progress value={course.progress} className="h-2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="payments" data-testid="student-dashboard-payments-table">
            <Card className="rounded-2xl">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.description')}</TableHead>
                      <TableHead>{t('dashboard.date')}</TableHead>
                      <TableHead>{t('dashboard.amount')}</TableHead>
                      <TableHead>{t('dashboard.status')}</TableHead>
                      <TableHead>{t('dashboard.payment_method')}</TableHead>
                      <TableHead>{t('dashboard.reference')}</TableHead>
                      <TableHead className="text-right">{t('dashboard.invoice')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                          {t('dashboard.no_payments')}
                        </TableCell>
                      </TableRow>
                    ) : summary.payments.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium text-sm">{p.description}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(p.date)}</TableCell>
                        <TableCell className="text-sm font-medium">{formatMoney(p.amount, p.currency || 'AED')}</TableCell>
                        <TableCell>
                          <Badge variant={p.status === 'paid' ? 'secondary' : 'outline'} className={`text-xs ${
                            p.status === 'paid' ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-primary/10 text-primary border-0'
                          }`}>
                            {paymentStatusLabel(p.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{p.payment_method || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{p.reference || '-'}</TableCell>
                        <TableCell className="text-right">
                          {p.invoice_pdf_url ? (
                            <Button type="button" size="sm" variant="outline" className="rounded-lg gap-2" onClick={() => downloadInvoice(p)}>
                              <Download className="h-3.5 w-3.5" /> PDF
                            </Button>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <div className="space-y-3">
              {notifications.length === 0 ? (
                <Card className="rounded-2xl border-dashed">
                  <CardContent className="p-6 text-center text-sm text-muted-foreground">
                    {t('dashboard.no_notifications')}
                  </CardContent>
                </Card>
              ) : notifications.map((item) => (
                <Card key={item.id} className="rounded-2xl border-primary/20">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-primary/10">
                      <Bell className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-medium">{item.title}</h4>
                        <span className="text-xs text-muted-foreground">{item.date}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{item.message}</p>
                      <div className="mt-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await markDone(item.id);
                              toast.success('Notification cleared.');
                            } catch {
                              toast.error(t('alerts.generic_error'));
                            }
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
